import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowDownLeft, ArrowUpRight, Coins, Eye, EyeOff, RefreshCw, Wallet } from "lucide-react";
import { api, ApiError } from "../api";
import { Button, Field, Modal, SelectField, Spinner } from "../components/ui";
import type { PublicWalletTransfer, WalletDirection, WalletOverview } from "../wallet-types";
import type { MobilePageProps } from "./types";
import { MobilePrimaryButton, PageHeader, RecordEmpty, SectionTitle, mobileMoney, useMobileNavigationGuard } from "./shared";
import "./wallet.css";
import { useWalletStatusPolling } from "./useWalletStatusPolling";

import { intentBlocksProvider, isDurableWalletReview, needsWalletReview, queryableTransfer, readIntent, rememberIntent, transferBlocksProvider, unresolved, validTransfer, walletSettlementSummary, walletTransferLabel, type TransferIntent } from "./wallet-intent";
export { walletTransferLabels } from "./wallet-intent";
type ProviderBalance = { value?: number; updatedAt?: string; busy?: boolean; error?: string };
const timeFormat = new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });

function requestError(reason: unknown, fallback: string) {
  return reason instanceof ApiError ? reason.status === 401 ? "登录已过期，请重新登录后查询原转账。" : reason.message : fallback;
}

function SettlementDetails({ transfer }: { transfer: PublicWalletTransfer }) {
  const settlement = walletSettlementSummary(transfer);
  if (!settlement) return null;
  return <dl className="wallet-settlement" aria-label="部分到账明细">
    <div><dt>申请金额</dt><dd>¥ {mobileMoney.format(settlement.requestedAmountCents / 100)}</dd></div>
    <div><dt>{settlement.actualLabel}</dt><dd>¥ {mobileMoney.format(settlement.actualAmountCents / 100)}</dd></div>
    <div><dt>{settlement.differenceLabel}</dt><dd>¥ {mobileMoney.format(settlement.differenceAmountCents / 100)}</dd></div>
  </dl>;
}

export function WalletPage({ profile, onBack, onNavigate, onRefresh, onLogin }: MobilePageProps) {
  const userId = profile?.user.id;
  const [wallet, setWallet] = useState<WalletOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [visible, setVisible] = useState(true);
  const [provider, setProvider] = useState("");
  const [direction, setDirection] = useState<WalletDirection>("deposit");
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [balances, setBalances] = useState<Record<string, ProviderBalance>>({});
  const [intent, setIntent] = useState<TransferIntent | null>(() => readIntent(userId));
  const [confirmation, setConfirmation] = useState<TransferIntent | null>(null);
  const [result, setResult] = useState<PublicWalletTransfer | null>(null);
  const [operationError, setOperationError] = useState("");
  const [busy, setBusy] = useState(false);
  const [lookupState, setLookupState] = useState<"idle" | "checking" | "missing" | "error">("idle");
  const owner = useRef({ mounted: false, userId, version: 0 });
  const walletRead = useRef<AbortController | null>(null);
  const resultRevision = useRef(0);
  const balanceReads = useRef(new Map<string, AbortController>());
  const mutationLock = useRef(false);
  const lookupLock = useRef(false);
  const amountRef = useRef<HTMLInputElement>(null);
  const refreshProfile = useRef(onRefresh);
  refreshProfile.current = onRefresh;
  const intentRef = useRef(intent);
  intentRef.current = intent;
  const selectedProvider = wallet?.providers.find(item => item.provider === provider);
  const balance = balances[provider];
  const pendingTransfers = wallet?.transfers.filter(unresolved) ?? [];
  const activeTransfer = transferBlocksProvider(result, provider) ? result : pendingTransfers.find(item => item.provider === provider);
  const pollingTransfer = queryableTransfer(activeTransfer) ? activeTransfer : null;
  const blockingIntent = intentBlocksProvider(intent, provider) ? intent : null;
  const blocked = Boolean(blockingIntent || activeTransfer);
  const disabledReason = !profile ? "登录后可使用场馆转账。" : loading ? "正在读取钱包信息，请稍候。" : error || !wallet ? "请先成功刷新钱包信息。" : !wallet.transfersEnabled ? "场馆转账暂未开放，仍可查询已登记场馆的余额及历史订单。" : blocked ? isDurableWalletReview(activeTransfer) ? "该场馆转账已停止自动查询，请联系客服按订单号人工核查；其他场馆仍可使用。" : needsWalletReview(activeTransfer) ? "转账待核查，请查询原单；不要重复转账。" : "转账自动确认中，无需后台批准；确认前请勿重复转账。" : !selectedProvider ? "请选择游戏场馆。" : direction === "withdrawal" && !selectedProvider.registered ? "该场馆尚无已登记账户，请先转入或进入场馆。" : "";
  useMobileNavigationGuard(Boolean(amount) && !intent, busy);

  const current = (member: string | undefined, version: number) => owner.current.mounted && owner.current.userId === member && owner.current.version === version;

  const reloadWallet = useCallback(async () => {
    const member = owner.current.userId;
    if (!owner.current.mounted || !member) return;
    walletRead.current?.abort();
    const controller = new AbortController();
    walletRead.current = controller;
    const version = owner.current.version;
    setLoading(true);
    setError("");
    try {
      const data = await api<WalletOverview>("/api/user/wallet", { cache: "no-store", signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15000)]) });
      if (!Number.isFinite(data.balance) || !Number.isFinite(data.reserved) || !Array.isArray(data.providers) || !Array.isArray(data.transfers)) throw new Error("Invalid wallet response");
      if (!controller.signal.aborted && current(member, version)) {
        resultRevision.current += 1;
        setWallet(data);
        setResult(value => value ? data.transfers.find(item => item.id === value.id) ?? value : value);
        const saved = intentRef.current;
        const existing = saved?.orderId ? data.transfers.find(item => item.id === saved.orderId) : undefined;
        if (saved && existing && validTransfer(existing, member, saved) && !queryableTransfer(existing)) {
          try { rememberIntent(member, null); } catch { /* The server result remains authoritative. */ }
          intentRef.current = null;
          setIntent(null);
          setResult(existing);
          setAmount("");
        }
        setProvider(value => data.providers.some(item => item.provider === value) ? value : data.providers.find(item => item.registered)?.provider ?? data.providers[0]?.provider ?? "");
      }
    } catch (reason) {
      if (!controller.signal.aborted && current(member, version)) setError(requestError(reason, "钱包信息读取失败。保留上次查询结果，请刷新后再操作。"));
    } finally {
      if (walletRead.current === controller) walletRead.current = null;
      if (!controller.signal.aborted && current(member, version)) setLoading(false);
    }
  }, []);

  const queryBalance = useCallback(async (code: string) => {
    const member = owner.current.userId;
    if (!owner.current.mounted || !member || !code || balanceReads.current.has(code)) return;
    const controller = new AbortController();
    balanceReads.current.set(code, controller);
    const version = owner.current.version;
    setBalances(values => ({ ...values, [code]: { ...values[code], busy: true, error: "" } }));
    try {
      const data = await api<{ provider: string; balance: number; updatedAt: string }>(`/api/user/wallet/providers/${encodeURIComponent(code)}/balance`, { cache: "no-store", signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15000)]) });
      if (data.provider !== code || !Number.isFinite(data.balance) || data.balance < 0 || !Number.isFinite(Date.parse(data.updatedAt))) throw new Error("Invalid balance response");
      if (!controller.signal.aborted && current(member, version)) setBalances(values => ({ ...values, [code]: { value: data.balance, updatedAt: data.updatedAt } }));
    } catch (reason) {
      if (!controller.signal.aborted && current(member, version)) setBalances(values => ({ ...values, [code]: { ...values[code], busy: false, error: requestError(reason, "场馆余额查询失败，请重试。") } }));
    } finally {
      if (balanceReads.current.get(code) === controller) balanceReads.current.delete(code);
    }
  }, []);

  function acceptTransfer(transfer: PublicWalletTransfer, expected?: TransferIntent) {
    if (!userId || !validTransfer(transfer, userId, expected)) throw new Error("Invalid transfer response");
    resultRevision.current += 1;
    setResult(transfer);
    setWallet(data => data ? { ...data, transfers: [transfer, ...data.transfers.filter(item => item.id !== transfer.id)] } : data);
    if (expected) {
      const next = queryableTransfer(transfer) ? { ...expected, orderId: transfer.id } : null;
      try { rememberIntent(userId, next); } catch { /* Keep the in-memory intent and server result if storage becomes unavailable. */ }
      setIntent(next);
      intentRef.current = next;
    }
    if (!queryableTransfer(transfer)) { setAmount(""); setBalances(values => { const next = { ...values }; delete next[transfer.provider]; return next; }); }
    setLookupState("idle");
  }

  async function lookupOriginal(saved: TransferIntent) {
    if (!userId || lookupLock.current || mutationLock.current) return;
    const member = userId;
    const version = owner.current.version;
    lookupLock.current = true;
    setLookupState("checking");
    setOperationError("");
    try {
      const transfer = await api<PublicWalletTransfer>(`/api/user/wallet/transfers/by-request/${encodeURIComponent(saved.clientId)}`, { cache: "no-store", signal: AbortSignal.timeout(15000) });
      if (current(member, version) && intentRef.current?.clientId === saved.clientId) {
        acceptTransfer(transfer, saved);
        await reloadWallet();
        try { await refreshProfile.current(); } catch { if (current(member, version)) setError("原单状态已更新，会员余额刷新失败，请重新刷新钱包。"); }
      }
    } catch (reason) {
      if (current(member, version)) {
        setLookupState(reason instanceof ApiError && reason.status === 404 ? "missing" : "error");
        if (!(reason instanceof ApiError && reason.status === 404)) setOperationError(requestError(reason, "原转账查询失败，请重试查询。不会自动重新转账。"));
      }
    } finally { if (current(member, version)) lookupLock.current = false; }
  }

  const statusPolling = useWalletStatusPolling(userId && !busy && !operationError.includes("登录已过期") && !error.includes("登录已过期") && (intent || pollingTransfer) ? `${userId}:${intent?.clientId ?? pollingTransfer?.id}` : null, async signal => {
    if (!userId || mutationLock.current || lookupLock.current || loading || walletRead.current) return;
    const member = userId, version = owner.current.version, revision = resultRevision.current;
    const saved = intentRef.current;
    const original = pollingTransfer;
    const transfer = saved
      ? await api<PublicWalletTransfer>(`/api/user/wallet/transfers/by-request/${encodeURIComponent(saved.clientId)}`, { cache: "no-store", signal })
      : null;
    if (signal.aborted || !current(member, version) || revision !== resultRevision.current || mutationLock.current || lookupLock.current || walletRead.current || (saved && intentRef.current?.clientId !== saved.clientId)) return;
    if (transfer) {
      acceptTransfer(transfer, saved ?? undefined); setOperationError("");
      if (!queryableTransfer(transfer)) {
        void reloadWallet(); void queryBalance(transfer.provider);
        void refreshProfile.current().catch(() => { if (current(member, version)) setError("原单已确认，会员余额刷新失败，请刷新钱包。"); });
      }
      return;
    }
    if (!original) return;
    const data = await api<WalletOverview>("/api/user/wallet", { cache: "no-store", signal });
    if (signal.aborted || !current(member, version) || revision !== resultRevision.current || mutationLock.current || lookupLock.current || walletRead.current || intentRef.current) return;
    const updated = data.transfers?.find(item => item.id === original.id);
    if (!updated || !validTransfer(updated, member) || updated.provider !== original.provider || updated.direction !== original.direction || updated.amountCents !== original.amountCents) throw new Error("Invalid original transfer");
    acceptTransfer(updated); setOperationError("");
    if (!queryableTransfer(updated)) {
      void reloadWallet(); void queryBalance(updated.provider);
      void refreshProfile.current().catch(() => { if (current(member, version)) setError("原单已确认，会员余额刷新失败，请刷新钱包。"); });
    }
  });

  useEffect(() => {
    const state = owner.current;
    state.mounted = true;
    state.userId = userId;
    state.version += 1;
    void reloadWallet();
    const saved = intentRef.current;
    if (saved) void lookupOriginal(saved);
    return () => {
      state.mounted = false;
      state.version += 1;
      lookupLock.current = false;
      walletRead.current?.abort();
      for (const controller of balanceReads.current.values()) controller.abort();
      balanceReads.current.clear();
    };
    // MemberPages keys this component by member identity; profile refreshes retain the draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, reloadWallet]);

  useEffect(() => {
    if (!busy) return;
    const beforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [busy]);

  function review(event: FormEvent) {
    event.preventDefault();
    if (!profile) { onLogin(); return; }
    if (disabledReason || mutationLock.current) return;
    const value = Number(amount);
    const message = !/^\d+(\.\d{1,2})?$/.test(amount.trim()) || !Number.isFinite(value) || value < 0.01 || value > 50000 ? "请输入 0.01 至 50,000 元，最多保留两位小数。" : direction === "deposit" && Math.round(value * 100) > Math.round((wallet?.balance ?? 0) * 100) ? "金额超过可用平台余额。" : "";
    setAmountError(message);
    if (message) { amountRef.current?.focus(); return; }
    setOperationError("");
    setConfirmation({ clientId: crypto.randomUUID(), provider, direction, amount: value.toFixed(2) });
  }

  async function confirmTransfer() {
    const request = confirmation;
    if (!request || !userId || mutationLock.current || lookupLock.current) return;
    const member = userId;
    const version = owner.current.version;
    try { rememberIntent(member, request); } catch { setOperationError("无法保留本次转账的查询凭据，请允许本站使用会话存储后重试。转账尚未提交。"); setConfirmation(null); return; }
    mutationLock.current = true;
    setIntent(request);
    intentRef.current = request;
    setBusy(true);
    setResult(null);
    setOperationError("");
    walletRead.current?.abort();
    balanceReads.current.get(request.provider)?.abort();
    balanceReads.current.delete(request.provider);
    setBalances(values => ({ ...values, [request.provider]: {} }));
    setLoading(false);
    try {
      const transfer = await api<PublicWalletTransfer>("/api/user/wallet/transfers", { method: "POST", signal: AbortSignal.timeout(25000), body: JSON.stringify({ provider: request.provider, direction: request.direction, amount: request.amount, clientId: request.clientId }) });
      if (current(member, version)) {
        acceptTransfer(transfer, request);
        setConfirmation(null);
        await reloadWallet();
        try { await refreshProfile.current(); } catch { if (current(member, version)) setError("转账状态已更新，会员余额刷新失败，请重新刷新钱包。"); }
      }
    } catch (reason) {
      if (current(member, version)) {
        setConfirmation(null);
        setLookupState("error");
        setOperationError(requestError(reason, "连接中断，转账结果尚未确认。请查询原单，不要创建新的转账。"));
      }
    } finally {
      mutationLock.current = false;
      if (current(member, version)) setBusy(false);
    }
  }

  async function reconcile(transfer: PublicWalletTransfer) {
    if (!userId || !queryableTransfer(transfer) || mutationLock.current || lookupLock.current) return;
    const member = userId;
    const version = owner.current.version;
    mutationLock.current = true;
    setBusy(true);
    setOperationError("");
    walletRead.current?.abort();
    setLoading(false);
    balanceReads.current.get(transfer.provider)?.abort();
    balanceReads.current.delete(transfer.provider);
    setBalances(values => ({ ...values, [transfer.provider]: {} }));
    try {
      const updated = await api<PublicWalletTransfer>(`/api/user/wallet/transfers/${encodeURIComponent(transfer.id)}/reconcile`, { method: "POST", body: "{}", signal: AbortSignal.timeout(25000) });
      if (current(member, version)) {
        acceptTransfer(updated, intentRef.current?.orderId === transfer.id ? intentRef.current : undefined);
        await reloadWallet();
        try { await refreshProfile.current(); } catch { if (current(member, version)) setError("原单状态已更新，会员余额刷新失败，请重新刷新钱包。"); }
      }
    } catch (reason) { if (current(member, version)) setOperationError(requestError(reason, "原单结果查询失败，请稍后再查；不会再次划转资金。")); }
    finally { mutationLock.current = false; if (current(member, version)) setBusy(false); }
  }

  return <div className="member-page member-wallet">
    <PageHeader title="我的钱包" onBack={onBack} action={<button className="member-text-button" type="button" disabled={busy} onClick={() => onNavigate("funds", { tab: "transfer" })}>转账记录</button>} />
    <div className="member-page__content">
      <div className="member-balance">
        <div className="member-balance__top"><span>可用平台余额 <button className="member-visibility" type="button" onClick={() => setVisible(value => !value)} aria-label={visible ? "隐藏钱包余额" : "显示钱包余额"}>{visible ? <Eye size={16} /> : <EyeOff size={16} />}</button></span><button type="button" disabled={loading || busy} aria-busy={loading} onClick={() => profile ? void reloadWallet() : onLogin()}><RefreshCw size={13} aria-hidden="true" /> {loading ? "刷新中" : "刷新"}</button></div>
        <strong className="member-balance__amount">¥ {visible ? wallet ? mobileMoney.format(wallet.balance) : "—" : "••••"}</strong>
        {wallet && wallet.reserved > 0 && <p className="wallet-reserved">待确认转入预留：¥ {visible ? mobileMoney.format(wallet.reserved) : "••••"}，已从可用余额扣除。</p>}
        <div className="member-wallet-actions"><button type="button" disabled={busy} onClick={() => onNavigate("deposit")}><span><Coins size={19} aria-hidden="true" /></span>充值</button><button type="button" disabled={busy} onClick={() => onNavigate("withdraw")}><span><Wallet size={18} aria-hidden="true" /></span>提现</button></div>
      </div>
      {error && <div className="member-inline-alert" role="alert">{error}{error.includes("登录已过期") && <button type="button" onClick={onLogin}>重新登录</button>}</div>}
      {!profile ? <RecordEmpty message="登录后查看钱包" action={<Button onClick={onLogin}>登录 / 注册</Button>}/> : <>
        <div className="member-white-card"><SectionTitle>场馆模式</SectionTitle><p>先核对场馆与金额，再确认转账。系统自动确认，无需后台批准；游戏入口确认转入后，成功会进入本次选择的游戏。离开游戏后，可在此转回平台。</p></div>
        {loading && !wallet ? <div className="wallet-loading"><Spinner label="正在读取钱包"/></div> : <div className="member-white-card wallet-transfer-panel">
          <SectionTitle>场馆余额与转账</SectionTitle>
          <SelectField label="游戏场馆" value={provider} disabled={busy || !wallet?.providers.length} aria-describedby={blocked ? "wallet-balance-readonly-note" : undefined} onChange={event => { setProvider(event.target.value); setAmountError(""); }}>
            {!wallet?.providers.length && <option value="">暂无可用场馆</option>}
            {wallet?.providers.map(item => <option key={item.provider} value={item.provider}>{item.provider}{item.registered ? "" : " · 尚未登记"}</option>)}
          </SelectField>
          {blocked && <p id="wallet-balance-readonly-note" className="wallet-note">可切换场馆查询余额；该场馆原转账未结案前，不能发起新转账。</p>}
          <div className="wallet-provider-balance"><div><span>{provider || "场馆"} 余额</span><strong>¥ {visible ? balance?.value === undefined ? "—" : mobileMoney.format(balance.value) : "••••"}</strong></div><Button emphasis="outline" busy={balance?.busy} disabled={!selectedProvider?.registered || busy} onClick={() => void queryBalance(provider)}>查询余额</Button></div>
          {balance?.error ? <p className="member-inline-error" role="alert">{balance.error}{balance.value !== undefined && " 当前保留上次查询的金额。"}</p> : balance?.updatedAt ? <p className="wallet-note">查询时间：{timeFormat.format(new Date(balance.updatedAt))}（北京时间）</p> : <p className="wallet-note">{selectedProvider && !selectedProvider.registered ? "首次转入时会建立场馆账户；尚未登记时无法查询余额或转出。" : "场馆余额尚未查询。"}</p>}
          <form noValidate onSubmit={review}>
            <div className="wallet-directions" role="group" aria-label="转账方向">
              <button type="button" aria-pressed={direction === "deposit"} className={direction === "deposit" ? "is-active" : ""} disabled={busy || blocked} onClick={() => { setDirection("deposit"); setAmountError(""); }}><ArrowDownLeft aria-hidden="true"/>平台转入场馆</button>
              <button type="button" aria-pressed={direction === "withdrawal"} className={direction === "withdrawal" ? "is-active" : ""} disabled={busy || blocked || !selectedProvider?.registered} onClick={() => { setDirection("withdrawal"); setAmountError(""); }}><ArrowUpRight aria-hidden="true"/>场馆转回平台</button>
            </div>
            <Field ref={amountRef} label="转账金额（元）" type="text" inputMode="decimal" autoComplete="off" value={amount} maxLength={10} disabled={busy || blocked || !wallet?.transfersEnabled} placeholder="请输入转账金额" error={amountError} hint="单笔 0.01–50,000 元，最多两位小数。" onChange={event => { setAmount(event.target.value); setAmountError(""); }}/>
            {disabledReason && <p id="wallet-transfer-disabled" className="wallet-note">{disabledReason}</p>}
            <MobilePrimaryButton type="submit" busy={busy} disabled={Boolean(disabledReason)} aria-describedby={disabledReason ? "wallet-transfer-disabled" : undefined}>核对转账</MobilePrimaryButton>
          </form>
        </div>}
        {intent && !pendingTransfers.some(item => item.id === intent.orderId) && (!result || result.id !== intent.orderId) && <div className="member-white-card wallet-recovery" role="status"><strong>上次转账结果尚未确认</strong><p>{intent.provider} · {intent.direction === "deposit" ? "平台转入场馆" : "场馆转回平台"} · ¥ {mobileMoney.format(Number(intent.amount))}</p><p>{lookupState === "missing" ? "暂未查询到原订单，不代表请求未执行。请保留原编号继续查询，或仅沿用相同金额继续原转账；不要新建转账。" : "系统自动确认，无需后台批准；确认前不会创建新的转账。"}</p><div className="wallet-recovery__actions"><Button emphasis="outline" busy={lookupState === "checking"} disabled={busy} onClick={() => void lookupOriginal(intent)}>查询原单</Button>{lookupState === "missing" && <><Button disabled={busy || !wallet?.transfersEnabled || Boolean(error)} onClick={() => setConfirmation(intent)}>继续原转账</Button></>}</div></div>}
        {statusPolling.notice && <div className="member-inline-alert" role="status">{statusPolling.notice}<Button emphasis="outline" disabled={busy} onClick={statusPolling.resume}>恢复状态更新</Button>{statusPolling.notice.includes("重新登录") && <Button emphasis="outline" onClick={onLogin}>重新登录</Button>}</div>}
        {operationError && <div className="member-inline-alert" role="alert">{operationError}{operationError.includes("登录已过期") && <button type="button" onClick={onLogin}>重新登录</button>}</div>}
        {(result || pendingTransfers.length > 0) && <div className="member-white-card wallet-orders"><SectionTitle>转账处理结果</SectionTitle>{[...(result ? [result] : []), ...pendingTransfers.filter(item => item.id !== result?.id)].map(transfer => <article key={transfer.id} className="wallet-order"><div className="wallet-order__heading"><strong>{transfer.provider} · {transfer.direction === "deposit" ? "转入场馆" : "转回平台"}</strong><span className={`wallet-status wallet-status--${transfer.status}`}>{walletTransferLabel(transfer)}</span></div><strong className="wallet-order__amount">¥ {mobileMoney.format(transfer.amountCents / 100)}</strong><SettlementDetails transfer={transfer}/><p className="wallet-note">订单号：{transfer.id}</p>{transfer.message && <p className="wallet-note">{transfer.message}</p>}{unresolved(transfer) && <p className="wallet-note">{isDurableWalletReview(transfer) ? "该订单已停止自动查询。请保留订单号联系客服人工核查；处理前请勿对同一场馆重复转账。" : needsWalletReview(transfer) ? "请保留订单号继续查询原单，必要时联系客服核查。结果未确认前请勿重复转账。" : transfer.direction === "deposit" ? "系统自动确认，无需后台批准。平台余额已预留；结果未确认前请勿重复转账。" : "系统自动确认，无需后台批准。场馆资金在结果确认后才计入平台余额；请勿重复转账。"}</p>}{queryableTransfer(transfer) && <Button emphasis="outline" busy={busy} disabled={lookupState === "checking"} onClick={() => void reconcile(transfer)}>查询原单结果</Button>}</article>)}</div>}
      </>}
    </div>
    <Modal open={Boolean(confirmation)} title="确认场馆转账" preventClose={busy} onClose={() => setConfirmation(null)}>
      {confirmation && <><dl className="wallet-confirm-details"><div><dt>从</dt><dd>{confirmation.direction === "deposit" ? "平台钱包" : `${confirmation.provider} 场馆`}</dd></div><div><dt>转至</dt><dd>{confirmation.direction === "deposit" ? `${confirmation.provider} 场馆` : "平台钱包"}</dd></div><div><dt>金额</dt><dd>¥ {mobileMoney.format(Number(confirmation.amount))}</dd></div></dl><p className="wallet-note">确认后将提交真实资金转账，请核对场馆、方向和金额。</p><div className="modal-actions"><Button data-autofocus intent="neutral" emphasis="outline" disabled={busy} onClick={() => setConfirmation(null)}>返回修改</Button><Button busy={busy} onClick={() => void confirmTransfer()}>确认转账</Button></div></>}
    </Modal>
  </div>;
}
